import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { PageHeader, StatCard, Chip, EmptyState } from "@/components/Shared";
import {
    Buildings, Bed, Storefront, Plus, Trash, Pause, Play, ArrowSquareOut, SignOut, Users as UsersIcon,
    CalendarCheck, Key, AddressBook, MagnifyingGlass, FunnelSimple, GearSix, ChartLineUp, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

// Account-type presentation — same underlying tenant DB/schema is reused for every
// type today (Hostel/PG and Business apps aren't built yet), only labels differ.
const ACCOUNT_TYPES = {
    community: { label: "Community", icon: Buildings, unitLabel: "Flats", peopleLabel: "Residents" },
    hostel_pg: { label: "Hostel / PG", icon: Bed, unitLabel: "Beds", peopleLabel: "Residents" },
    business: { label: "Business", icon: Storefront, unitLabel: "Vendors", peopleLabel: "Staff" },
};
const CREATE_FIELD_META = {
    community: { nameLabel: "Community / Society Name", namePlaceholder: "Green Valley Residency", adminLabel: "First Admin Name" },
    hostel_pg: { nameLabel: "Hostel / PG Name", namePlaceholder: "Sunrise PG", adminLabel: "First Admin Name" },
    business: { nameLabel: "Business Name", namePlaceholder: "Metro Facility Services", adminLabel: "Business / Vendor Admin Name" },
};

function typeMeta(t) { return ACCOUNT_TYPES[t] || ACCOUNT_TYPES.community; }

function getSubscriptionStatus(society) {
    if (!society.subscription) return { status: "unknown", label: "No plan", variant: "neutral" };
    const endDate = new Date(society.subscription.subscription_end_date);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { status: "expired", label: "Expired", variant: "danger", daysLeft };
    if (daysLeft <= 7) return { status: "expiring", label: `${daysLeft}d left`, variant: "warn", daysLeft };
    return { status: "active", label: society.subscription.plan_type.replace('_', ' '), variant: "success", daysLeft };
}

function formatDate(isoString) {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function MasterConsole() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const [societies, setSocieties] = useState([]);
    const [rollup, setRollup] = useState(null);
    const [agents, setAgents] = useState([]);
    const [demoRequests, setDemoRequests] = useState([]);
    const [tab, setTab] = useState(searchParams.get("tab") === "demo-requests" ? "demo-requests" : "accounts");

    // Filters
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");

    // Create account
    const [openCreate, setOpenCreate] = useState(false);
    const [form, setForm] = useState({ account_type: "community", name: "", admin_name: "", admin_contact_email: "", admin_phone: "" });
    const [createResult, setCreateResult] = useState(null);

    // Create master user
    const [agentOpen, setAgentOpen] = useState(false);
    const [agentForm, setAgentForm] = useState({ name: "", email: "", password: "", role: "support" });

    // Subscription management
    const [subOpen, setSubOpen] = useState(false);
    const [selectedSociety, setSelectedSociety] = useState(null);
    const [subForm, setSubForm] = useState({
        plan_type: "free",
        subscription_start_date: new Date().toISOString().split('T')[0],
        trial_days: 30
    });

    // Reset user password
    const [resetOpen, setResetOpen] = useState(false);
    const [resetSociety, setResetSociety] = useState(null);
    const [resetUsers, setResetUsers] = useState([]);
    const [resetUsersLoading, setResetUsersLoading] = useState(false);
    const [resetTargetUser, setResetTargetUser] = useState(null);
    const [resetPassword, setResetPassword] = useState("");
    const [resetSaving, setResetSaving] = useState(false);

    // Account detail
    const [detailAccount, setDetailAccount] = useState(null);

    const isSuper = user?.role === "super_admin";

    async function load() {
        try {
            const [s, r, d] = await Promise.all([api.get("/master/societies"), api.get("/master/rollup"), api.get("/master/demo-requests")]);
            setSocieties(s.data); setRollup(r.data); setDemoRequests(d.data);
            if (isSuper) {
                const a = await api.get("/master/users");
                setAgents(a.data);
            }
        } catch (e) { toast.error(formatError(e)); }
    }
    useEffect(() => { load(); }, []);

    const filteredAccounts = useMemo(() => {
        return societies.filter((s) => {
            const atype = s.account_type || "community";
            if (typeFilter !== "all" && atype !== typeFilter) return false;
            if (statusFilter !== "all") {
                const subStatus = getSubscriptionStatus(s);
                if (statusFilter === "active" && !(s.status === "active" && subStatus.status !== "expired")) return false;
                if (statusFilter === "suspended" && s.status !== "suspended") return false;
                if (statusFilter === "trial" && s.subscription?.plan_type !== "free") return false;
                if (statusFilter === "expired" && subStatus.status !== "expired") return false;
            }
            if (search.trim()) {
                const q = search.trim().toLowerCase();
                const hay = [s.name, s.slug, s.first_admin_email, s.first_admin_name, s.admin_contact_email].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [societies, typeFilter, statusFilter, search]);

    async function updateLeadStatus(reqId, status) {
        try {
            await api.patch(`/master/demo-requests/${reqId}`, { status });
            toast.success("Lead updated");
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function createAccount(e) {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (!payload.admin_contact_email || !payload.admin_contact_email.trim()) delete payload.admin_contact_email;
            if (!payload.admin_phone || !payload.admin_phone.trim()) delete payload.admin_phone;
            const { data } = await api.post("/master/societies", payload);
            setCreateResult(data);
            toast.success(`${form.name} created`);
            setForm({ account_type: "community", name: "", admin_name: "", admin_contact_email: "", admin_phone: "" });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    function closeCreateDialog() {
        setOpenCreate(false);
        setCreateResult(null);
    }
    async function toggleStatus(s) {
        const next = s.status === "active" ? "suspended" : "active";
        try { await api.patch(`/master/societies/${s.id}/status`, { status: next }); toast.success(`${s.name}: ${next}`); load(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function removeSociety(s) {
        if (!confirm(`Delete ${s.name} and ALL its data? This cannot be undone.`)) return;
        if (!confirm(`Type-through confirm: This will drop the ${s.name} database. Proceed?`)) return;
        try { await api.delete(`/master/societies/${s.id}`); toast.success(`${s.name} deleted`); setDetailAccount(null); load(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function impersonate(s) {
        try {
            const { data } = await api.post(`/master/societies/${s.id}/impersonate`);
            const masterToken = localStorage.getItem("maintayn_token");
            if (masterToken) localStorage.setItem("maintayn_master_token_backup", masterToken);
            localStorage.setItem("maintayn_impersonating", JSON.stringify({ society_name: s.name, by: data.impersonated_by }));
            if (data.access_token) localStorage.setItem("maintayn_token", data.access_token);
            toast.success(`Impersonating ${s.name} as ${data.user.name}`);
            nav("/app");
            window.location.reload();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function createAgent(e) {
        e.preventDefault();
        try {
            await api.post("/master/users", agentForm);
            toast.success("Master user created");
            setAgentOpen(false);
            setAgentForm({ name: "", email: "", password: "", role: "support" });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function toggleAgent(u) {
        try { await api.patch(`/master/users/${u.id}`, { is_active: !u.is_active }); toast.success("Updated"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function removeAgent(u) {
        if (!confirm(`Remove ${u.name}?`)) return;
        try { await api.delete(`/master/users/${u.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    function openSubscription(s) {
        setSelectedSociety(s);
        const sub = s.subscription || {};
        setSubForm({
            plan_type: sub.plan_type || "free",
            subscription_start_date: sub.subscription_start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
            trial_days: sub.trial_days || 30
        });
        setSubOpen(true);
    }

    async function updateSubscription(e) {
        e.preventDefault();
        try {
            const payload = {
                plan_type: subForm.plan_type,
                subscription_start_date: subForm.subscription_start_date
            };
            if (subForm.plan_type === "free") {
                payload.trial_days = parseInt(subForm.trial_days);
            }
            const { data } = await api.patch(`/master/societies/${selectedSociety.id}/subscription`, payload);
            toast.success("Subscription updated");
            setSubOpen(false);
            if (detailAccount && detailAccount.id === selectedSociety.id) setDetailAccount({ ...detailAccount, subscription: data.subscription });
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function openResetPassword(s) {
        setResetSociety(s);
        setResetTargetUser(null);
        setResetPassword("");
        setResetUsers([]);
        setResetOpen(true);
        setResetUsersLoading(true);
        try {
            const { data } = await api.get(`/master/societies/${s.id}/users`);
            setResetUsers(data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setResetUsersLoading(false); }
    }

    async function submitResetPassword(e) {
        e.preventDefault();
        if (!resetTargetUser) return;
        setResetSaving(true);
        try {
            await api.post(`/master/societies/${resetSociety.id}/users/${resetTargetUser.id}/reset-password`, { new_password: resetPassword });
            toast.success(`Password reset for ${resetTargetUser.email}`);
            setResetOpen(false);
        } catch (e) { toast.error(formatError(e)); }
        finally { setResetSaving(false); }
    }

    const fieldMeta = CREATE_FIELD_META[form.account_type] || CREATE_FIELD_META.community;

    return (
        <div className="min-h-screen bg-brand-bg">
            <header className="bg-brand-ink text-white">
                <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-action">Master console</p>
                        <h1 className="font-heading text-2xl">maintayn · Customer Success</h1>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-medium" data-testid="master-user-name">{user?.name}</p>
                        <p className="text-xs text-[#9CA3AF]">{user?.email} · {user?.role}</p>
                        <button data-testid="master-logout" onClick={async () => { await logout(); nav("/login"); }}
                            className="mt-2 text-xs bg-white/10 hover:bg-brand-action rounded-full px-3 py-1 inline-flex items-center gap-1 transition-colors">
                            <SignOut size={12} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                {/* Overview */}
                {rollup && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8" data-testid="master-overview-cards">
                        <StatCard testId="stat-total-accounts" label="Total Accounts" value={rollup.total_accounts ?? rollup.societies} />
                        <StatCard testId="stat-total-communities" label="Communities" value={rollup.total_communities ?? 0} />
                        <StatCard testId="stat-total-hostel-pg" label="Hostel / PG" value={rollup.total_hostel_pg ?? 0} />
                        <StatCard testId="stat-total-businesses" label="Businesses" value={rollup.total_businesses ?? 0} />
                        <StatCard testId="stat-total-users" label="Total Users" value={rollup.total_users ?? rollup.total_residents} />
                        <StatCard testId="stat-outstanding" label="Outstanding / Unpaid" value={rollup.total_unpaid_invoices} accent />
                        <StatCard testId="stat-active-subs" label="Active Subscriptions" value={rollup.active_subscriptions ?? 0} />
                    </div>
                )}

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="bg-white border border-brand-line rounded-full p-1 mb-6 flex-wrap h-auto">
                        <TabsTrigger data-testid="master-tab-accounts" value="accounts" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                            <Buildings size={14} className="mr-1.5" /> All Accounts
                        </TabsTrigger>
                        <TabsTrigger data-testid="master-tab-demo-requests" value="demo-requests" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                            <AddressBook size={14} className="mr-1.5" /> Demo Requests
                            {demoRequests.filter(d => d.status === "new").length > 0 && (
                                <span className="ml-1.5 bg-brand-action text-white rounded-full text-[10px] px-1.5 py-0.5">{demoRequests.filter(d => d.status === "new").length}</span>
                            )}
                        </TabsTrigger>
                        {isSuper && (
                            <TabsTrigger data-testid="master-tab-agents" value="agents" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                                <UsersIcon size={14} className="mr-1.5" /> Support Team
                            </TabsTrigger>
                        )}
                        <TabsTrigger data-testid="master-tab-analytics" value="analytics" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                            <ChartLineUp size={14} className="mr-1.5" /> Platform Analytics
                        </TabsTrigger>
                        <TabsTrigger data-testid="master-tab-settings" value="settings" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-white px-5">
                            <GearSix size={14} className="mr-1.5" /> Settings
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="accounts">
                        <PageHeader
                            overline="Rollup"
                            title="All Accounts"
                            description="Create, monitor, manage, suspend, and support every account on maintayn."
                            actions={isSuper && (
                                <Button data-testid="master-create-account-btn" onClick={() => setOpenCreate(true)}
                                    className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                                    <Plus size={16} className="mr-1.5" /> Create Account
                                </Button>
                            )}
                        />

                        {/* Filters */}
                        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5" data-testid="account-filters">
                            <div className="flex items-center gap-1.5 bg-white border border-brand-line rounded-full p-1 flex-wrap">
                                {[
                                    { key: "all", label: "All Accounts" },
                                    { key: "community", label: "Communities" },
                                    { key: "hostel_pg", label: "Hostel / PG" },
                                    { key: "business", label: "Businesses" },
                                ].map((f) => (
                                    <button key={f.key} data-testid={`filter-type-${f.key}`} onClick={() => setTypeFilter(f.key)}
                                        className={`text-xs font-medium rounded-full px-3 py-1.5 transition-colors ${typeFilter === f.key ? "bg-brand-ink text-white" : "text-brand-inkSoft hover:bg-brand-bg"}`}>
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger data-testid="filter-status" className="rounded-full border-brand-line h-9 text-xs w-[150px] bg-white">
                                    <FunnelSimple size={12} className="mr-1" /> <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="suspended">Suspended</SelectItem>
                                    <SelectItem value="trial">Trial / Free</SelectItem>
                                    <SelectItem value="expired">Expired</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="relative flex-1 md:max-w-xs">
                                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                                <Input data-testid="account-search" value={search} onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search accounts, DB slug, admin name or email…" className="rounded-full border-brand-line pl-9 h-9 text-xs bg-white" />
                            </div>
                        </div>

                        {filteredAccounts.length === 0 ? (
                            <EmptyState title="No accounts found" description="Try adjusting your filters, or create the first account." />
                        ) : (
                            <>
                                {/* Desktop table */}
                                <div className="hidden lg:block bg-white border border-brand-line rounded-sm overflow-hidden overflow-x-auto">
                                    <table className="w-full text-sm" data-testid="accounts-table">
                                        <thead>
                                            <tr className="bg-brand-bg border-b border-brand-line text-left">
                                                {["Account", "Type", "Admin", "Units / Users", "Unpaid", "Subscription", "Status", ""].map(h => (
                                                    <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAccounts.map(s => {
                                                const subStatus = getSubscriptionStatus(s);
                                                const meta = typeMeta(s.account_type);
                                                const TypeIcon = meta.icon;
                                                return (
                                                    <tr key={s.id} data-testid={`society-row-${s.id}`} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                                        <td className="py-3 px-4">
                                                            <button data-testid={`account-open-detail-${s.id}`} onClick={() => setDetailAccount(s)} className="text-left">
                                                                <div className="font-medium text-brand-ink hover:text-brand-action">{s.name}</div>
                                                                <div className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{s.is_default ? "default" : "custom"} · {new Date(s.created_at).toLocaleDateString()}</div>
                                                                {s.slug && <div data-testid={`society-slug-${s.id}`} className="text-[10px] font-mono text-brand-inkSoft/80 mt-0.5">soc_{s.slug}</div>}
                                                            </button>
                                                        </td>
                                                        <td className="py-3 px-4">
                                                            <Chip variant="default"><TypeIcon size={10} className="inline mr-1 -mt-0.5" />{meta.label}</Chip>
                                                        </td>
                                                        <td className="py-3 px-4 text-brand-inkSoft">{s.first_admin_email}</td>
                                                        <td className="py-3 px-4 text-brand-ink">
                                                            <span className="font-heading font-semibold">{s.flats}</span> {meta.unitLabel} · <span className="font-heading font-semibold">{s.residents}</span> {meta.peopleLabel}
                                                        </td>
                                                        <td className="py-3 px-4 text-brand-ink font-heading">{s.unpaid_invoices}</td>
                                                        <td className="py-3 px-4">
                                                            <Chip variant={subStatus.variant}>{subStatus.label}</Chip>
                                                            {s.subscription && (
                                                                <div className="text-[10px] text-brand-inkSoft mt-1">
                                                                    {formatDate(s.subscription.subscription_start_date)} — {formatDate(s.subscription.subscription_end_date)}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="py-3 px-4"><Chip variant={s.status === "active" ? "success" : "danger"}>{s.status}</Chip></td>
                                                        <td className="py-3 px-4 text-right space-x-1 whitespace-nowrap">
                                                            <button data-testid={`subscription-${s.id}`} onClick={() => openSubscription(s)}
                                                                title="Manage subscription" className="p-1.5 hover:bg-brand-sage rounded-sm inline-flex"><CalendarCheck size={14} /></button>
                                                            <button data-testid={`impersonate-${s.id}`} onClick={() => impersonate(s)}
                                                                title="Impersonate admin" className="p-1.5 hover:bg-brand-sage rounded-sm inline-flex"><ArrowSquareOut size={14} /></button>
                                                            <button data-testid={`reset-password-${s.id}`} onClick={() => openResetPassword(s)}
                                                                title="Reset a user's password" className="p-1.5 hover:bg-brand-sage rounded-sm inline-flex"><Key size={14} /></button>
                                                            {isSuper && (
                                                                <>
                                                                    <button data-testid={`toggle-${s.id}`} onClick={() => toggleStatus(s)}
                                                                        title={s.status === "active" ? "Suspend" : "Reactivate"}
                                                                        className="p-1.5 hover:bg-brand-sage rounded-sm inline-flex">
                                                                        {s.status === "active" ? <Pause size={14} /> : <Play size={14} />}
                                                                    </button>
                                                                    {!s.is_default && (
                                                                        <button data-testid={`delete-${s.id}`} onClick={() => removeSociety(s)}
                                                                            title="Delete account" className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action inline-flex">
                                                                            <Trash size={14} />
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile / tablet cards */}
                                <div className="lg:hidden space-y-3" data-testid="accounts-cards">
                                    {filteredAccounts.map((s) => {
                                        const subStatus = getSubscriptionStatus(s);
                                        const meta = typeMeta(s.account_type);
                                        const TypeIcon = meta.icon;
                                        return (
                                            <button key={s.id} data-testid={`account-card-${s.id}`} onClick={() => setDetailAccount(s)}
                                                className="w-full text-left bg-white border border-brand-line rounded-lg p-4 space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <div className="font-medium text-brand-ink">{s.name}</div>
                                                    <Chip variant={s.status === "active" ? "success" : "danger"}>{s.status}</Chip>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-brand-inkSoft">
                                                    <Chip variant="default"><TypeIcon size={10} className="inline mr-1 -mt-0.5" />{meta.label}</Chip>
                                                    <Chip variant={subStatus.variant}>{subStatus.label}</Chip>
                                                </div>
                                                <div className="text-xs text-brand-inkSoft">{s.first_admin_email}</div>
                                                {s.slug && <div data-testid={`society-slug-card-${s.id}`} className="text-[10px] font-mono text-brand-inkSoft/80">soc_{s.slug}</div>}
                                                <div className="text-xs text-brand-ink">{s.flats} {meta.unitLabel} · {s.residents} {meta.peopleLabel} · {s.unpaid_invoices} unpaid</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="demo-requests">
                        {demoRequests.length === 0 ? (
                            <EmptyState title="No demo requests yet" description="Leads submitted from the maintayn.in 'Book a Demo' form will show up here." />
                        ) : (
                            <div className="bg-white border border-brand-line rounded-sm overflow-hidden overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-brand-bg border-b border-brand-line text-left">
                                            {["Society / Community", "Contact", "City", "Flats", "Message", "Email", "Submitted", "Status"].map(h => (
                                                <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {demoRequests.map(d => (
                                            <tr key={d.id} data-testid={`demo-request-row-${d.id}`} className="border-b border-brand-line/60 hover:bg-brand-bg/50 align-top">
                                                <td className="py-3 px-4">
                                                    <div className="font-medium text-brand-ink">{d.society_name}</div>
                                                    <div className="text-[10px] text-brand-inkSoft">{d.name}</div>
                                                </td>
                                                <td className="py-3 px-4 text-brand-inkSoft">
                                                    <div>{d.email}</div>
                                                    <div>{d.phone}</div>
                                                </td>
                                                <td className="py-3 px-4 text-brand-inkSoft">{d.city || "—"}</td>
                                                <td className="py-3 px-4 text-brand-inkSoft">{d.approx_flats || "—"}</td>
                                                <td className="py-3 px-4 text-brand-inkSoft max-w-xs truncate" title={d.message || ""}>{d.message || "—"}</td>
                                                <td className="py-3 px-4"><Chip variant={d.email_status === "sent" ? "success" : d.email_status === "failed" ? "danger" : "neutral"}>{d.email_status}</Chip></td>
                                                <td className="py-3 px-4 text-brand-inkSoft whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</td>
                                                <td className="py-3 px-4">
                                                    <Select value={d.status} onValueChange={(v) => updateLeadStatus(d.id, v)}>
                                                        <SelectTrigger data-testid={`demo-request-status-${d.id}`} className="rounded-full border-brand-line h-8 text-xs w-[120px]">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="new">New</SelectItem>
                                                            <SelectItem value="contacted">Contacted</SelectItem>
                                                            <SelectItem value="converted">Converted</SelectItem>
                                                            <SelectItem value="closed">Closed</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </TabsContent>

                    {isSuper && (
                        <TabsContent value="agents">
                            <div className="flex justify-end mb-4">
                                <Button data-testid="master-create-agent-btn" onClick={() => setAgentOpen(true)}
                                    className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                                    <Plus size={16} className="mr-1.5" /> Add support agent
                                </Button>
                            </div>
                            <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-brand-bg border-b border-brand-line text-left">
                                            {["Name", "Email", "Role", "Status", ""].map(h => (
                                                <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {agents.map(a => (
                                            <tr key={a.id} data-testid={`agent-row-${a.id}`} className="border-b border-brand-line/60">
                                                <td className="py-3 px-4 font-medium text-brand-ink">{a.name}</td>
                                                <td className="py-3 px-4 text-brand-inkSoft">{a.email}</td>
                                                <td className="py-3 px-4"><Chip variant={a.role === "super_admin" ? "danger" : "default"}>{a.role}</Chip></td>
                                                <td className="py-3 px-4"><Chip variant={a.is_active ? "success" : "neutral"}>{a.is_active ? "active" : "inactive"}</Chip></td>
                                                <td className="py-3 px-4 text-right space-x-1">
                                                    <button data-testid={`toggle-agent-${a.id}`} onClick={() => toggleAgent(a)}
                                                        className="text-xs text-brand-action hover:underline mr-2">{a.is_active ? "Deactivate" : "Activate"}</button>
                                                    {a.id !== user.id && (
                                                        <button data-testid={`delete-agent-${a.id}`} onClick={() => removeAgent(a)}
                                                            className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action inline-flex"><Trash size={14} /></button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </TabsContent>
                    )}

                    <TabsContent value="analytics">
                        <EmptyState
                            image={null}
                            title="Platform Analytics — Coming Soon"
                            description="Cross-account usage trends, growth, and revenue analytics will live here."
                            action={<Chip variant="warn"><Sparkle size={10} className="inline mr-1 -mt-0.5" />Coming soon</Chip>}
                        />
                    </TabsContent>
                    <TabsContent value="settings">
                        <EmptyState
                            image={null}
                            title="Master Console Settings — Coming Soon"
                            description="Platform-wide configuration and preferences will live here."
                            action={<Chip variant="warn"><Sparkle size={10} className="inline mr-1 -mt-0.5" />Coming soon</Chip>}
                        />
                    </TabsContent>
                </Tabs>
            </main>

            {/* Create account dialog */}
            <Dialog open={openCreate} onOpenChange={(v) => { if (!v) closeCreateDialog(); else setOpenCreate(true); }}>
                <DialogContent className="rounded-sm max-w-lg">
                    {createResult ? (
                        <div className="space-y-4" data-testid="account-create-success">
                            <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Account created</DialogTitle></DialogHeader>
                            <p className="text-sm text-brand-inkSoft">Share these login details with the new admin. They'll be asked to set their own password on first sign-in.</p>
                            <div className="bg-brand-bg rounded-sm p-4 space-y-1.5 font-mono text-sm">
                                <div data-testid="create-result-login"><span className="text-brand-inkSoft">Login email: </span><b>{createResult.admin_login_email}</b></div>
                                <div data-testid="create-result-password"><span className="text-brand-inkSoft">Temp password: </span><b>{createResult.admin_temp_password}</b></div>
                            </div>
                            <DialogFooter>
                                <Button data-testid="create-result-close" onClick={closeCreateDialog} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Done</Button>
                            </DialogFooter>
                        </div>
                    ) : (
                        <>
                            <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Create New Account</DialogTitle></DialogHeader>
                            <form onSubmit={createAccount} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Account Type</Label>
                                    <div className="grid grid-cols-3 gap-2" data-testid="create-account-type">
                                        {Object.entries(ACCOUNT_TYPES).map(([key, meta]) => {
                                            const Icon = meta.icon;
                                            return (
                                                <button type="button" key={key} data-testid={`account-type-option-${key}`}
                                                    onClick={() => setForm({ ...form, account_type: key })}
                                                    className={`flex flex-col items-center gap-1.5 rounded-sm border py-3 px-2 text-xs font-medium transition-colors ${form.account_type === key ? "border-brand-action bg-brand-sage text-brand-ink" : "border-brand-line text-brand-inkSoft hover:bg-brand-bg"}`}>
                                                    <Icon size={18} weight={form.account_type === key ? "fill" : "regular"} />
                                                    {meta.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="space-y-2"><Label>{fieldMeta.nameLabel}</Label>
                                    <Input data-testid="account-name-field" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                                        placeholder={fieldMeta.namePlaceholder} className="rounded-sm border-brand-line" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2"><Label>{fieldMeta.adminLabel}</Label>
                                        <Input data-testid="account-admin-name" required value={form.admin_name} onChange={e => setForm({ ...form, admin_name: e.target.value })}
                                            className="rounded-sm border-brand-line" /></div>
                                    <div className="space-y-2"><Label>Admin Phone</Label>
                                        <Input value={form.admin_phone} onChange={e => setForm({ ...form, admin_phone: e.target.value })}
                                            className="rounded-sm border-brand-line" /></div>
                                </div>
                                <div className="space-y-2"><Label>Admin Contact Email (optional)</Label>
                                    <Input data-testid="account-admin-contact-email" type="email" value={form.admin_contact_email} onChange={e => setForm({ ...form, admin_contact_email: e.target.value })}
                                        placeholder="For reference / onboarding email only" className="rounded-sm border-brand-line" /></div>
                                <p className="text-xs text-brand-inkSoft">A login email will be auto-generated (admin@&lt;name-slug&gt;.in) with a temporary password. The admin must set their own password on first sign-in.</p>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={closeCreateDialog} className="rounded-full">Cancel</Button>
                                    <Button type="submit" data-testid="account-create-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Create Account</Button>
                                </DialogFooter>
                            </form>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Create master user dialog */}
            <Dialog open={agentOpen} onOpenChange={setAgentOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Add support agent</DialogTitle></DialogHeader>
                    <form onSubmit={createAgent} className="space-y-4">
                        <div className="space-y-2"><Label>Name</Label>
                            <Input required value={agentForm.name} onChange={e => setAgentForm({ ...agentForm, name: e.target.value })} className="rounded-sm border-brand-line" /></div>
                        <div className="space-y-2"><Label>Email</Label>
                            <Input data-testid="agent-email" type="email" required value={agentForm.email} onChange={e => setAgentForm({ ...agentForm, email: e.target.value })} className="rounded-sm border-brand-line" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2"><Label>Password</Label>
                                <Input required minLength={6} value={agentForm.password} onChange={e => setAgentForm({ ...agentForm, password: e.target.value })} className="rounded-sm border-brand-line font-mono" /></div>
                            <div className="space-y-2"><Label>Role</Label>
                                <select value={agentForm.role} onChange={e => setAgentForm({ ...agentForm, role: e.target.value })}
                                    className="rounded-sm border border-brand-line h-10 px-3 bg-white w-full">
                                    <option value="support">support</option>
                                    <option value="super_admin">super_admin</option>
                                </select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setAgentOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="agent-create-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Add</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Subscription management dialog */}
            <Dialog open={subOpen} onOpenChange={setSubOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">
                            Manage Subscription
                        </DialogTitle>
                        {selectedSociety && (
                            <p className="text-sm text-brand-inkSoft">{selectedSociety.name}</p>
                        )}
                    </DialogHeader>
                    <form onSubmit={updateSubscription} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Plan Type</Label>
                            <select
                                data-testid="sub-plan-type"
                                value={subForm.plan_type}
                                onChange={e => setSubForm({ ...subForm, plan_type: e.target.value })}
                                className="rounded-sm border border-brand-line h-10 px-3 bg-white w-full">
                                <option value="free">Free (Trial)</option>
                                <option value="1_year">1 Year Plan</option>
                                <option value="2_year">2 Year Plan</option>
                                <option value="3_year">3 Year Plan</option>
                                <option value="4_year">4 Year Plan</option>
                                <option value="5_year">5 Year Plan</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <Label>Subscription Start Date</Label>
                            <Input
                                data-testid="sub-start-date"
                                type="date"
                                required
                                value={subForm.subscription_start_date}
                                onChange={e => setSubForm({ ...subForm, subscription_start_date: e.target.value })}
                                className="rounded-sm border-brand-line"
                            />
                        </div>

                        {subForm.plan_type === "free" && (
                            <div className="space-y-2">
                                <Label>Trial Period (Days)</Label>
                                <Input
                                    data-testid="sub-trial-days"
                                    type="number"
                                    required
                                    min="1"
                                    max="365"
                                    value={subForm.trial_days}
                                    onChange={e => setSubForm({ ...subForm, trial_days: e.target.value })}
                                    className="rounded-sm border-brand-line"
                                />
                                <p className="text-xs text-brand-inkSoft">Default is 30 days. Can be extended up to 365 days.</p>
                            </div>
                        )}

                        {selectedSociety?.subscription && (
                            <div className="bg-brand-bg rounded-sm p-4 space-y-2">
                                <p className="text-xs uppercase tracking-overline text-brand-inkSoft">Current Subscription</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <span className="text-brand-inkSoft">Plan:</span>
                                        <span className="ml-2 font-medium">{selectedSociety.subscription.plan_type.replace('_', ' ')}</span>
                                    </div>
                                    <div>
                                        <span className="text-brand-inkSoft">Ends:</span>
                                        <span className="ml-2 font-medium">{formatDate(selectedSociety.subscription.subscription_end_date)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setSubOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="sub-update-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Update Subscription</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Reset user password dialog */}
            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">Reset password</DialogTitle>
                        {resetSociety && <p className="text-sm text-brand-inkSoft">{resetSociety.name}</p>}
                    </DialogHeader>
                    <form onSubmit={submitResetPassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Find user (name or email)</Label>
                            {resetTargetUser ? (
                                <div className="flex items-center justify-between rounded-sm border border-brand-line px-3 py-2 bg-brand-bg">
                                    <div>
                                        <p className="text-sm font-medium text-brand-ink" data-testid="reset-selected-user-name">{resetTargetUser.name}</p>
                                        <p className="text-xs text-brand-inkSoft">{resetTargetUser.email} · {resetTargetUser.role}</p>
                                    </div>
                                    <button type="button" data-testid="reset-change-user-btn" className="text-xs text-brand-action hover:underline"
                                        onClick={() => setResetTargetUser(null)}>Change</button>
                                </div>
                            ) : (
                                <Command className="rounded-sm border border-brand-line" data-testid="reset-user-search">
                                    <CommandInput placeholder="Type a name or email…" />
                                    <CommandList>
                                        {resetUsersLoading ? (
                                            <div className="py-6 text-center text-sm text-brand-inkSoft">Loading users…</div>
                                        ) : (
                                            <>
                                                <CommandEmpty>No matching user</CommandEmpty>
                                                <CommandGroup>
                                                    {resetUsers.map((u) => (
                                                        <CommandItem key={u.id} value={`${u.name} ${u.email}`}
                                                            data-testid={`reset-user-option-${u.id}`}
                                                            onSelect={() => setResetTargetUser(u)}>
                                                            <div>
                                                                <span className="font-medium">{u.name}</span>
                                                                <span className="text-brand-inkSoft ml-2 text-xs">{u.email} · {u.role}</span>
                                                            </div>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </>
                                        )}
                                    </CommandList>
                                </Command>
                            )}
                        </div>
                        {resetTargetUser && (
                            <div className="space-y-2">
                                <Label>New temporary password</Label>
                                <Input data-testid="reset-password-input" required minLength={6} value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    placeholder="e.g. Welcome@2026" className="rounded-sm border-brand-line font-mono" />
                                <p className="text-xs text-brand-inkSoft">The user will be required to set a new password on their next sign-in — share this temporary one with them directly.</p>
                            </div>
                        )}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setResetOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="reset-password-submit" disabled={!resetTargetUser || resetPassword.length < 6 || resetSaving}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {resetSaving ? "Resetting…" : "Reset password"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Account detail dialog */}
            <Dialog open={!!detailAccount} onOpenChange={(v) => !v && setDetailAccount(null)}>
                <DialogContent className="rounded-sm max-w-2xl" data-testid="account-detail-dialog">
                    {detailAccount && (() => {
                        const meta = typeMeta(detailAccount.account_type);
                        const TypeIcon = meta.icon;
                        const subStatus = getSubscriptionStatus(detailAccount);
                        return (
                            <div className="space-y-6">
                                <DialogHeader>
                                    <div className="flex items-center gap-2">
                                        <DialogTitle className="font-heading text-2xl tracking-tight">{detailAccount.name}</DialogTitle>
                                        <Chip variant="default"><TypeIcon size={10} className="inline mr-1 -mt-0.5" />{meta.label}</Chip>
                                        <Chip variant={detailAccount.status === "active" ? "success" : "danger"}>{detailAccount.status}</Chip>
                                    </div>
                                </DialogHeader>

                                <div>
                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-semibold mb-2">Account Overview</p>
                                    <div className="grid grid-cols-2 gap-3 text-sm bg-brand-bg rounded-sm p-4">
                                        <div><span className="text-brand-inkSoft">Admin login: </span><span className="font-medium">{detailAccount.first_admin_email}</span></div>
                                        <div><span className="text-brand-inkSoft">Admin name: </span><span className="font-medium">{detailAccount.first_admin_name}</span></div>
                                        {detailAccount.slug && (
                                            <div><span className="text-brand-inkSoft">DB slug: </span><span data-testid="detail-db-slug" className="font-mono font-medium select-all">soc_{detailAccount.slug}</span></div>
                                        )}
                                        {detailAccount.admin_contact_email && (
                                            <div><span className="text-brand-inkSoft">Contact email: </span><span className="font-medium">{detailAccount.admin_contact_email}</span></div>
                                        )}
                                        <div><span className="text-brand-inkSoft">Created: </span><span className="font-medium">{formatDate(detailAccount.created_at)}</span></div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-semibold mb-2">Usage</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <StatCard label={meta.unitLabel} value={detailAccount.flats} />
                                        <StatCard label={meta.peopleLabel} value={detailAccount.residents} />
                                        <StatCard label="Unpaid Invoices" value={detailAccount.unpaid_invoices} accent />
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-semibold mb-2">Subscription</p>
                                    <div className="flex items-center justify-between bg-brand-bg rounded-sm p-4">
                                        <div className="text-sm space-y-1">
                                            <div><span className="text-brand-inkSoft">Plan: </span><span className="font-medium">{detailAccount.subscription?.plan_type?.replace('_', ' ') || "—"}</span> <Chip variant={subStatus.variant}>{subStatus.label}</Chip></div>
                                            <div className="text-brand-inkSoft text-xs">{formatDate(detailAccount.subscription?.subscription_start_date)} — {formatDate(detailAccount.subscription?.subscription_end_date)}</div>
                                        </div>
                                        <Button data-testid="detail-manage-subscription" variant="outline" className="rounded-full" onClick={() => { setDetailAccount(null); openSubscription(detailAccount); }}>
                                            <CalendarCheck size={14} className="mr-1.5" /> Manage
                                        </Button>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-semibold mb-2">Platform Actions</p>
                                    <div className="flex flex-wrap gap-2">
                                        <Button data-testid="detail-impersonate" variant="outline" className="rounded-full" onClick={() => impersonate(detailAccount)}>
                                            <ArrowSquareOut size={14} className="mr-1.5" /> Open / Impersonate
                                        </Button>
                                        <Button data-testid="detail-reset-password" variant="outline" className="rounded-full" onClick={() => { setDetailAccount(null); openResetPassword(detailAccount); }}>
                                            <Key size={14} className="mr-1.5" /> Reset Password
                                        </Button>
                                        {isSuper && (
                                            <Button data-testid="detail-toggle-status" variant="outline" className="rounded-full" onClick={() => { toggleStatus(detailAccount); setDetailAccount(null); }}>
                                                {detailAccount.status === "active" ? <Pause size={14} className="mr-1.5" /> : <Play size={14} className="mr-1.5" />}
                                                {detailAccount.status === "active" ? "Suspend" : "Reactivate"}
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {isSuper && !detailAccount.is_default && (
                                    <div className="border-t border-[#FECACA] pt-4">
                                        <p className="text-[10px] uppercase tracking-overline text-brand-action font-semibold mb-2">Destructive</p>
                                        <Button data-testid="detail-delete-account" variant="outline" className="rounded-full border-brand-action text-brand-action hover:bg-[#FEF2F2]" onClick={() => removeSociety(detailAccount)}>
                                            <Trash size={14} className="mr-1.5" /> Delete Account
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}
